import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, signal } from '@angular/core';
import { PdfDocument } from '../../../shared/components/pdf-document/pdf-document';
import { LoaderService } from '../../../shared/services/loader-service';
import { ToastService } from '../../../shared/services/toast-service';
import { HttpClientService } from '../../../shared/services/http-client-service';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { ExtratoDesligamentoTokenResponse } from '../../../core/models/extrato-desligamento';
import { DetalhesMVPResponse } from '../../../core/models/resumo';
import { base64ParaPdf } from '../../../shared/utils/pdf';
import { formatDate } from '@angular/common';
import { Router } from '@angular/router';

@Component({
  imports: [PdfDocument],
  selector: 'app-detalhes-mvp',
  styleUrl: './detalhes-mvp.scss',
  templateUrl: './detalhes-mvp.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DetalhesMvp {
  readonly pdfSrc = signal<string | undefined>(undefined);
  private readonly httpClientService = inject(HttpClientService);
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly openModal = signal(false);
  readonly detalhes = signal<DetalhesMVPResponse | null>(null);
  readonly pdfSrcTermo = signal<string | undefined>(undefined);

  readonly textoVisualizado = computed(() => {
    const dataAssinatura = this.detalhes()?.dataAssinaturaTermo;
    return dataAssinatura
      ? `Assinado com sucesso em ${formatDate(dataAssinatura, "dd/MM/yyyy 'às' HH:mm", 'en-US')}`
      : '';
  });
  
  ngOnInit(): void {
    void this.carregarDetalhes();
    void this.getExtratoDesligamento();
  }

  protected readonly exibicao = computed(() => {
    const detalhes = this.detalhes();
    if (!detalhes) return null;

    return {
      protocolo: detalhes.protocolo
    };
  });
  
  protected async carregarDetalhes(): Promise<void> {
    this.loaderService.showLoader('Carregando...');

    try {
      const { body } = await this.httpClientService.getDetalhesMVP();
      if (!body) throw new Error('Detalhes sem corpo');
      this.detalhes.set(body);
      this.carregarTermo(body.termoPdf);
    } catch {
      this.toastService.show('Não foi possível carregar os detalhes. Tente novamente.', 'error');
    } finally {
      this.loaderService.hideLoader();
    }
  }

  async download(): Promise<void> {
    const endereco = this.pdfSrc();

    if (!endereco) {
      this.toastService.show('Não há extrato disponível para download.', 'error');
      return;
    }

    try {
      await this.baixarArquivo(endereco);
    } catch {
      this.toastService.show('Não foi possível baixar o extrato.', 'error');
    }
  }

  async onSubmit() {
    this.openModal.set(true);
  }

  closeModal(): void {
    this.openModal.set(false);
  }

  async cancelar(): Promise<void> {
    this.loaderService.showLoader('Cancelando...');
    try {
      await this.httpClientService.cancelarEscolhaInstituto();
      this.toastService.show('A escolha de Instituto Online foi cancelada com sucesso.', 'success');
      this.router.navigate(['instituto-online/home']);
    } catch (erro) {
      console.error('Falha ao cancelar o Instituto Online', erro);
      this.toastService.show('Não foi possível cancelar a escolha de Instituto Online. Tente novamente.', 'error');
    } finally {
      this.loaderService.hideLoader();
      this.closeModal();
    }
  }

  private async baixarArquivo(endereco: string): Promise<void> {
    const resposta = await fetch(endereco);
    const blob = await resposta.blob();
    const objectUrl = URL.createObjectURL(blob);

    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = 'extrato-desligamento.pdf';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();

    URL.revokeObjectURL(objectUrl);
  }

  private async getExtratoDesligamento(): Promise<void> {
    this.loaderService.showLoader('Gerando extrato...');

    try {
      const { body }: HttpResponse<ExtratoDesligamentoTokenResponse> =
        await this.httpClientService.gerarExtratoDesligamentoPorToken();
      this.pdfSrc.set(body?.urlExtrato);
    } catch (erro) {
      const semTokenValido = erro instanceof HttpErrorResponse && (erro.status === 401 || erro.status === 403);
      this.toastService.show(
        semTokenValido
          ? 'Não foi possível identificar os dados do token.'
          : 'Não foi possível gerar o extrato.',
        'error',
      );
    } finally {
      this.loaderService.hideLoader();
    }
  }

  private carregarTermo(termoPdf: string | undefined): void {
      if (!termoPdf) return;
  
      try {
        const url = URL.createObjectURL(base64ParaPdf(termoPdf));
        this.pdfSrcTermo.set(url);
        this.destroyRef.onDestroy(() => URL.revokeObjectURL(url));
      } catch {
        this.toastService.show('Não foi possível carregar o termo.', 'error');
      }
    }
}
